import * as React from 'react';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
  InfoHint
} from '@bliks-io/ui';
import { DepartmentSelect, type DepartmentSelectProps } from './DepartmentSelect';

export interface FormFieldDepartmentSelectProps extends DepartmentSelectProps {
  /**
   * Field label
   */
  label?: string;

  /**
   * Optional explanatory tooltip rendered as an info icon next to the label.
   */
  labelHint?: React.ReactNode;

  /**
   * Field description/helper text
   */
  description?: string;

  /**
   * Error message
   */
  error?: string;

  /**
   * Mark field as required
   */
  required?: boolean;

  /**
   * Field orientation
   */
  orientation?: 'vertical' | 'horizontal' | 'responsive';

  /**
   * Additional class for Field wrapper
   */
  fieldClassName?: string;

  /**
   * Field name
   */
  name?: string;
}

/**
 * DepartmentSelect with Field integration
 * Wraps base DepartmentSelect with label, description, and error display
 * For use without React Hook Form. For RHF integration, use FormDepartmentSelect instead.
 *
 * @example
 * ```tsx
 * const [department, setDepartment] = useState<string | undefined>();
 *
 * <FormFieldDepartmentSelect
 *   value={department}
 *   onChange={setDepartment}
 *   label="Department"
 *   description="Select a department"
 *   required
 * />
 * ```
 */
export const FormFieldDepartmentSelect = React.forwardRef<
  HTMLButtonElement,
  FormFieldDepartmentSelectProps
>(
  (
    {
      label,
      labelHint,
      description,
      error,
      required = false,
      orientation = 'vertical',
      fieldClassName,
      name,
      id,
      ...selectProps
    },
    ref
  ) => {
    const fieldId = id || name;
    const hasError = !!error;

    return (
      <Field orientation={orientation} className={fieldClassName} data-invalid={hasError}>
        {label && (
          <FieldLabel htmlFor={fieldId} className="flex items-center gap-1.5">
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
            {labelHint && <InfoHint content={labelHint} />}
          </FieldLabel>
        )}
        <FieldContent>
          {description && <FieldDescription>{description}</FieldDescription>}
          <DepartmentSelect {...selectProps} id={fieldId} ref={ref} />
          {error && <FieldError>{error}</FieldError>}
        </FieldContent>
      </Field>
    );
  }
);

FormFieldDepartmentSelect.displayName = 'FormFieldDepartmentSelect';
